import React, { createContext, useContext, useState, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/lib/i18n";
import { startCheckout } from "@/lib/razorpay";
import api, { setSubscriptionRequiredHandler, isSubscriptionError } from "@/lib/api";
import { getReceiptBlob, saveBlob, shareBlob, receiptName } from "@/lib/receipt";
import { toast } from "sonner";
import { X, Check, ShieldCheck, Sparkles, Loader2, AlertTriangle, Download, Gift, Share2 } from "lucide-react";

const PlanContext = createContext(null);

const INCLUDED = [
  "Save daily entries & history calendar",
  "Daily, Weekly, Monthly (MDCF), Annual & UC reports",
  "PDF & Excel downloads",
  "Share reports on WhatsApp",
  "Valid for 365 days",
];

export function PlanProvider({ children }) {
  const { user, refresh } = useAuth();
  const { t } = useLang();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [opts, setOpts] = useState(null);
  const [paying, setPaying] = useState(false);
  const [success, setSuccess] = useState(null);
  const [price, setPrice] = useState(99);

  const plan = user && user.subscription ? user.subscription : null;
  const hasAccess = !user ? false : (user.role === "admin" ? true : (plan ? plan.has_access : false));

  useEffect(() => {
    if (plan && plan.price) setPrice(plan.price);
    else api.get("/payments/config").then(({ data }) => setPrice(data.price)).catch(() => {});
  }, [plan]);

  const openUpgrade = useCallback((o) => { setOpts(o || null); setOpen(true); }, []);
  const closeUpgrade = useCallback(() => { setOpen(false); setOpts(null); }, []);

  // Any 402 from the shared axios interceptor opens this sheet (mobile + desktop alike).
  useEffect(() => {
    setSubscriptionRequiredHandler((detail) => {
      setOpts(detail && detail.limit != null ? { trial: true, limit: detail.limit } : null);
      setOpen(true);
    });
    return () => setSubscriptionRequiredHandler(null);
  }, []);

  const payNow = useCallback(async (hooks) => {
    const h = (hooks && typeof hooks === "object" && ("onSuccess" in hooks || "onFailure" in hooks)) ? hooks : {};
    if (!user) { window.location.href = "/register"; return; }
    setPaying(true);
    await startCheckout({
      onSuccess: async (v) => {
        setPaying(false);
        setOpen(false);
        setOpts(null);
        await refresh();
        setSuccess(v);
        try { h.onSuccess && h.onSuccess(v); } catch {}
      },
      onFailure: (msg) => { setPaying(false); toast.error(msg || "Payment failed. Please try again."); try { h.onFailure && h.onFailure(msg); } catch {} },
      onDismiss: () => { setPaying(false); },
    });
  }, [user, refresh]);

  // Returns true if the error was a subscription 402 (the sheet is already opening
  // via the shared interceptor; callers use this to skip their own error toast).
  const handle402 = useCallback((e) => {
    if (isSubscriptionError(e)) {
      const d = e.response?.data?.detail || {};
      setOpts(d && d.limit != null ? { trial: true, limit: d.limit } : null);
      setOpen(true);
      return true;
    }
    return false;
  }, []);

  const goRefer = useCallback(() => { setOpen(false); setOpts(null); nav("/app/refer"); }, [nav]);

  const value = { plan, hasAccess, price, openUpgrade, closeUpgrade, payNow, paying, handle402 };

  return (
    <PlanContext.Provider value={value}>
      {children}
      {open && <UpgradeSheet price={price} paying={paying} onPay={payNow} onClose={closeUpgrade} isGuest={!user} t={t} opts={opts} onRefer={goRefer} />}
      {success && <SuccessModal data={success} onClose={() => setSuccess(null)} />}
    </PlanContext.Provider>
  );
}

function UpgradeSheet({ price, paying, onPay, onClose, isGuest, t, opts, onRefer }) {
  const trial = opts?.trial;
  return (
    <div className="fixed inset-0 z-[90] flex items-end md:items-center justify-center" data-testid="upgrade-sheet">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full md:max-w-md bg-thali-paper rounded-t-3xl md:rounded-3xl p-5 shadow-2xl animate-fade-in-up max-h-[85vh] overflow-y-auto">
        <button onClick={onClose} className="absolute right-4 top-4 text-thali-ink/50" aria-label="Close" data-testid="upgrade-close"><X className="w-5 h-5" /></button>
        <div className="w-12 h-1.5 bg-thali-turmeric/40 rounded-full mx-auto mb-4 md:hidden" />
        <div className="flex items-center gap-2 text-thali-green font-heading font-extrabold text-xl">
          <Sparkles className="w-6 h-6" /> {trial ? `${t("used_all_pre")} ${opts.limit} ${t("free_calcs")} 🎉` : t("upgrade_title")}
        </div>
        <p className="text-thali-ink/70 text-sm mt-1">{trial ? t("upgrade_trial_sub") : t("upgrade_sub")}</p>

        <div className="mt-4 rounded-2xl bg-thali-green text-white p-4 text-center">
          <div className="text-sm opacity-80">{t("annual_plan")}</div>
          <div className="text-4xl font-num font-extrabold">₹{price}<span className="text-base font-semibold opacity-80">/{t("year")}</span></div>
          <div className="text-xs opacity-80 mt-1">{t("incl_taxes")}</div>
        </div>

        <ul className="mt-4 space-y-2">
          {INCLUDED.map((f, i) => (
            <li key={i} className="flex items-start gap-2 text-sm">
              <Check className="w-4 h-4 text-thali-green mt-0.5 shrink-0" /> {f}
            </li>
          ))}
        </ul>

        <button onClick={onPay} disabled={paying} className="btn-3d-green w-full mt-5 text-lg" data-testid="upgrade-pay-btn">
          {paying ? <><Loader2 className="w-5 h-5 animate-spin" /> {t("processing")}</> : (isGuest ? t("register_to_subscribe") : `${t("pay")} ₹${price}`)}
        </button>
        {!isGuest && (
          <a href="/pay-qr" className="block text-center w-full mt-2 text-sm font-bold text-thali-ink/70 py-2" data-testid="upgrade-qr-btn">📷 {t("qr_or_scan")}</a>
        )}
        {trial && !isGuest && (
          <button onClick={onRefer} className="btn-ghost w-full mt-2 text-sm text-thali-saffron font-bold flex items-center justify-center gap-2" data-testid="upgrade-refer-btn">
            <Gift className="w-4 h-4" /> {t("refer_get_free")}
          </button>
        )}
        <div className="flex items-center justify-center gap-1 text-xs text-thali-ink/50 mt-3">
          <ShieldCheck className="w-3.5 h-3.5" /> {t("secure_razorpay")}
        </div>
      </div>
    </div>
  );
}

function SuccessModal({ data, onClose }) {
  const [downloading, setDownloading] = useState(false);
  const [err, setErr] = useState("");
  const v = data.validity || {};
  const fmt = (iso) => { try { return new Date(iso).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }); } catch { return "—"; } };

  const ensureBlob = async () => {
    const { blob, error } = await getReceiptBlob(data.payment_id);
    if (error) { setErr(error); return null; }
    setErr("");
    return blob;
  };

  const downloadReceipt = async () => {
    setDownloading(true);
    const blob = await ensureBlob();
    if (blob) { const ok = saveBlob(blob, receiptName(data.payment_id)); if (!ok) toast.message("Receipt opened in a new tab."); }
    setDownloading(false);
  };

  const shareReceiptPdf = async () => {
    setDownloading(true);
    const blob = await ensureBlob();
    if (blob) { const ok = await shareBlob(blob, receiptName(data.payment_id)); if (!ok && saveBlob(blob, receiptName(data.payment_id))) toast.message("Receipt downloaded."); }
    setDownloading(false);
  };

  const shareReferral = async () => {
    try {
      const { data: r } = await api.get("/referral/me");
      const msg = (r.share_or || "") + (r.share_en ? "\n\n———\n\n" + r.share_en : "");
      window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, "_blank");
    } catch { toast.error("Could not open share."); }
  };

  return (
    <div className="fixed inset-0 z-[95] flex items-center justify-center p-4" data-testid="payment-success">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-sm bg-thali-paper rounded-3xl p-6 text-center shadow-2xl animate-fade-in-up max-h-[90vh] overflow-y-auto">
        <div className="w-20 h-20 rounded-full bg-thali-green/15 grid place-items-center mx-auto mb-3 animate-scale-pop">
          <div className="w-14 h-14 rounded-full bg-thali-green grid place-items-center"><Check className="w-8 h-8 text-white" strokeWidth={3} /></div>
        </div>
        <h2 className="font-heading font-extrabold text-2xl text-thali-green">Payment successful!</h2>
        <p className="text-thali-ink/70 text-sm mt-1">Your Annual Plan is now active.</p>
        <div className="mt-4 rounded-xl bg-white border border-thali-turmeric/30 p-3 text-sm">
          <div className="flex justify-between py-1"><span className="text-thali-ink/60">Valid from</span><span className="font-semibold">{fmt(v.validity_from)}</span></div>
          <div className="flex justify-between py-1"><span className="text-thali-ink/60">Valid until</span><span className="font-semibold">{fmt(v.validity_to)}</span></div>
        </div>
        <div className="grid grid-cols-2 gap-2 mt-4">
          <button onClick={downloadReceipt} disabled={downloading} className="btn-3d-saffron" data-testid="success-receipt-btn">
            {downloading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />} {downloading ? "Preparing…" : "Receipt"}
          </button>
          <button onClick={shareReceiptPdf} disabled={downloading} className="btn-ghost" data-testid="success-receipt-share-btn">
            <Share2 className="w-4 h-4" /> Share
          </button>
        </div>
        {err && <p className="text-xs text-amber-700 mt-2" data-testid="success-receipt-error">{err}</p>}
        <div className="mt-3 rounded-xl bg-thali-turmeric/20 p-3">
          <div className="text-sm font-semibold text-thali-ink flex items-center gap-1"><Sparkles className="w-4 h-4 text-thali-saffron" /> Share with teachers: get 1 year FREE + 3-month recharge</div>
          <button onClick={shareReferral} className="btn-3d-green w-full mt-2 text-sm" data-testid="success-share-btn">Share on WhatsApp</button>
        </div>
        <button onClick={onClose} className="btn-ghost w-full mt-2" data-testid="success-close-btn">Continue</button>
      </div>
    </div>
  );
}

export function usePlan() {
  const ctx = useContext(PlanContext);
  if (!ctx) throw new Error("usePlan must be used within PlanProvider");
  return ctx;
}
